"use client";

import { createClient } from "../supabase/client";
import { humanOwnerId } from "../taskOwnership";
import { isTaskReportChannel } from "../taskReportOrigin";
import type { FocusLabel, FocusPreference, FocusProject, FocusTask } from "../personalFocus";
import type { Database } from "../../types/database";

export type PersonalFocusErrorCode = "invalid_input" | "unauthorized" | "unavailable" |
  "query_failed" | "invalid_response" | "aborted" | "session_changed";

export class PersonalFocusDataError extends Error {
  constructor(readonly code: PersonalFocusErrorCode) {
    super(`Personal focus: ${code}`);
    this.name = "PersonalFocusDataError";
  }
}

export const PERSONAL_FOCUS_PAGE_SIZE = 500;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FOCUS_COLUMNS = "id,identifier,workspace_id,team_id,title,description,status,priority,assignee_id,owner_id,executor_agent_id,project_id,due_date,created_at,updated_at,report_channel,report_context,report_url,deleted_at,issue_labels(label_id)";
const PREFERENCE_COLUMNS = "workspace_id,user_id,issue_id,confirmed_at";
const OPEN_STATUSES = new Set(["in_progress", "todo", "backlog", "in_review"]);
const PRIORITIES = { none: 0, urgent: 1, high: 2, medium: 3, low: 4 } as const;
type Client = ReturnType<typeof createClient>;
type IssueRow = Pick<Database["public"]["Tables"]["issues"]["Row"],
  "id" | "identifier" | "workspace_id" | "team_id" | "title" | "description" |
  "status" | "priority" | "assignee_id" | "owner_id" | "executor_agent_id" |
  "project_id" | "due_date" | "created_at" | "updated_at" |
  "report_channel" | "report_context" | "report_url" | "deleted_at"
> & { issue_labels: { label_id: string }[] };
type PreferenceRow = { workspace_id: string; user_id: string; issue_id: string; confirmed_at: string };
type Context = { client: Client; workspaceId: string; userId: string; signal?: AbortSignal; check: () => void };

function fail(code: PersonalFocusErrorCode): never { throw new PersonalFocusDataError(code); }
function normalizedId(value: string): string {
  if (!UUID.test(value)) fail("invalid_input");
  return value.toLowerCase();
}
function stamp(value: string): number {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) fail("invalid_response");
  return parsed;
}

async function verifyPrincipal(context: Context) {
  const { client, workspaceId, userId, signal, check } = context;
  check();
  const { data, error } = await client.auth.getUser();
  check();
  if (error || data.user?.id !== userId) fail("unauthorized");
  let profileQuery = client.from("profiles").select("id,is_bot").eq("id", userId);
  let memberQuery = client.from("workspace_members").select("workspace_id,user_id,role")
    .eq("workspace_id", workspaceId).eq("user_id", userId);
  if (signal) { profileQuery = profileQuery.abortSignal(signal); memberQuery = memberQuery.abortSignal(signal); }
  const [profile, member] = await Promise.all([profileQuery.maybeSingle(), memberQuery.maybeSingle()]);
  check();
  if (profile.error || member.error) fail("query_failed");
  if (profile.data?.id !== userId || profile.data.is_bot !== false ||
    member.data?.workspace_id !== workspaceId || member.data.user_id !== userId ||
    member.data.role === "bot" || member.data.role === "bot_readonly") fail("unavailable");
}

/** Remember identity changes even if the browser later switches back. No global state is mutated. */
async function withPersonalContext<T>(
  rawWorkspaceId: string, rawUserId: string, signal: AbortSignal | undefined,
  operation: (context: Context) => Promise<T>,
): Promise<T> {
  const workspaceId = normalizedId(rawWorkspaceId);
  const userId = normalizedId(rawUserId);
  if (signal?.aborted) fail("aborted");
  let identityChanged = false;
  let closed = false;
  let unsubscribe = () => {};
  const check = () => {
    if (closed || signal?.aborted) fail("aborted");
    if (identityChanged) fail("session_changed");
  };
  try {
    const client = createClient();
    const { data: { subscription } } = client.auth.onAuthStateChange((_event, session) => {
      if (session?.user?.id !== userId) identityChanged = true;
    });
    unsubscribe = () => subscription.unsubscribe();
    const context = { client, workspaceId, userId, signal, check };
    await verifyPrincipal(context);
    const result = await operation(context);
    await verifyPrincipal(context);
    return result;
  } catch (error) {
    if (error instanceof PersonalFocusDataError) throw error;
    check();
    fail("query_failed");
  } finally {
    closed = true;
    unsubscribe();
  }
}

function mapTask(row: IssueRow, workspaceId: string, userId: string): FocusTask {
  if (!Array.isArray(row.issue_labels) || row.issue_labels.some((label) => !label || !UUID.test(label.label_id))) fail("invalid_response");
  const task: FocusTask = {
    id: row.id, identifier: row.identifier, workspaceId: row.workspace_id, teamId: row.team_id,
    title: row.title, description: row.description, status: row.status,
    priority: PRIORITIES[row.priority], assigneeId: row.assignee_id,
    ownerId: row.owner_id, executorAgentId: row.executor_agent_id, projectId: row.project_id,
    labelIds: [...new Set(row.issue_labels.map((label) => label.label_id))].sort(),
    dueDate: row.due_date ? stamp(row.due_date) : null,
    createdAt: stamp(row.created_at), updatedAt: stamp(row.updated_at),
    reportChannel: isTaskReportChannel(row.report_channel) ? row.report_channel : null,
    reportContext: row.report_context, reportUrl: row.report_url,
  };
  if (!UUID.test(task.id) || task.workspaceId !== workspaceId || humanOwnerId(task) !== userId ||
    row.deleted_at !== null || !OPEN_STATUSES.has(task.status) || task.priority === undefined) fail("invalid_response");
  return task;
}

function mapPreference(row: PreferenceRow | null, workspaceId: string, userId: string): FocusPreference | null {
  if (!row) return null;
  if (row.workspace_id !== workspaceId || row.user_id !== userId || !UUID.test(row.issue_id)) fail("invalid_response");
  return { issueId: row.issue_id, confirmedAt: stamp(row.confirmed_at) };
}

async function loadTasks({ client, workspaceId, userId, signal, check }: Context): Promise<FocusTask[]> {
  const tasks: FocusTask[] = [];
  const seen = new Set<string>();
  let offset = 0;
  for (;;) {
    check();
    let query = client.from("issues").select(FOCUS_COLUMNS, { count: "exact" })
      .eq("workspace_id", workspaceId).is("deleted_at", null)
      .not("status", "in", "(done,canceled)")
      .or(`owner_id.eq.${userId},and(owner_id.is.null,executor_agent_id.is.null,assignee_id.eq.${userId})`)
      .order("id", { ascending: true }).range(offset, offset + PERSONAL_FOCUS_PAGE_SIZE - 1);
    if (signal) query = query.abortSignal(signal);
    const { data, error, count } = await query;
    check();
    if (error) fail("query_failed");
    const page = data ?? [];
    for (const row of page) {
      const task = mapTask(row, workspaceId, userId);
      if (seen.has(task.id)) fail("invalid_response");
      seen.add(task.id);
      tasks.push(task);
    }
    offset += page.length;
    if (count != null && (!Number.isSafeInteger(count) || count < 0)) fail("invalid_response");
    if (count != null && offset >= count) return tasks;
    if (!page.length) {
      if (count != null && offset < count) fail("query_failed");
      return tasks;
    }
  }
}

/** Existing catalog rows are read under the same verified viewer and exact workspace. */
async function loadCatalog(context: Context): Promise<{ labels: FocusLabel[]; projects: FocusProject[] }> {
  const { client, workspaceId, signal, check } = context;
  async function pages<T extends { id: string }>(queryPage: (offset: number) => PromiseLike<{ data: unknown; error: unknown; count: number | null }>, map: (row: Record<string, unknown>) => T): Promise<T[]> {
    const rows: T[] = []; const seen = new Set<string>();
    for (;;) {
      check();
      const { data, error, count } = await queryPage(rows.length);
      check();
      if (error) fail("query_failed");
      if (!Array.isArray(data) || (count !== null && (!Number.isSafeInteger(count) || count < 0))) fail("invalid_response");
      for (const raw of data) {
        if (!raw || typeof raw !== "object" || Array.isArray(raw)) fail("invalid_response");
        const row = map(raw as Record<string, unknown>);
        if (!UUID.test(row.id) || seen.has(row.id)) fail("invalid_response");
        seen.add(row.id); rows.push(row);
      }
      if (count !== null && rows.length > count) fail("invalid_response");
      if (count !== null && rows.length === count) return rows;
      if (!data.length) {
        if (count !== null && rows.length < count) fail("query_failed");
        return rows;
      }
    }
  }
  const [labels, projects] = await Promise.all([
    pages((offset) => {
      let query = client.from("labels").select("id,name,team_id,teams!inner(workspace_id,name)", { count: "exact" })
        .eq("teams.workspace_id", workspaceId).order("id", { ascending: true }).range(offset, offset + PERSONAL_FOCUS_PAGE_SIZE - 1);
      if (signal) query = query.abortSignal(signal);
      return query;
    }, (row): FocusLabel => {
      const team = row.teams;
      if (!team || typeof team !== "object" || Array.isArray(team) || !("workspace_id" in team) || team.workspace_id !== workspaceId ||
        !("name" in team) || typeof team.name !== "string" || !team.name.trim() ||
        typeof row.id !== "string" || typeof row.team_id !== "string" || !UUID.test(row.team_id) || typeof row.name !== "string" || !row.name.trim()) fail("invalid_response");
      return { id: row.id, name: row.name, teamId: row.team_id, teamName: team.name, workspaceId };
    }),
    pages((offset) => {
      let query = client.from("projects").select("id,workspace_id,name", { count: "exact" })
        .eq("workspace_id", workspaceId).order("id", { ascending: true }).range(offset, offset + PERSONAL_FOCUS_PAGE_SIZE - 1);
      if (signal) query = query.abortSignal(signal);
      return query;
    }, (row): FocusProject => {
      if (row.workspace_id !== workspaceId || typeof row.id !== "string" || typeof row.name !== "string" || !row.name.trim()) fail("invalid_response");
      return { id: row.id, name: row.name, workspaceId };
    }),
  ]);
  return { labels, projects };
}

export async function loadPersonalFocus(
  workspaceId: string, userId: string, signal?: AbortSignal,
): Promise<{ tasks: FocusTask[]; preference: FocusPreference | null; labels: FocusLabel[]; projects: FocusProject[] }> {
  return withPersonalContext(workspaceId, userId, signal, async (context) => {
    const { client, workspaceId, userId, check } = context;
    let query = client.from("personal_task_focus").select(PREFERENCE_COLUMNS)
      .eq("workspace_id", workspaceId).eq("user_id", userId);
    if (signal) query = query.abortSignal(signal);
    const [tasks, preference, catalog] = await Promise.all([loadTasks(context), query.maybeSingle(), loadCatalog(context)]);
    check();
    if (preference.error) fail("query_failed");
    return { tasks, preference: mapPreference(preference.data, workspaceId, userId), ...catalog };
  });
}

export async function confirmPersonalFocus(
  workspaceId: string, userId: string, issueId: string, signal?: AbortSignal,
): Promise<FocusPreference> {
  const targetId = normalizedId(issueId);
  return withPersonalContext(workspaceId, userId, signal, async ({ client, workspaceId, userId, check }) => {
    let query = client.from("personal_task_focus")
      .upsert({ workspace_id: workspaceId, user_id: userId, issue_id: targetId }, { onConflict: "workspace_id,user_id" })
      .select(PREFERENCE_COLUMNS);
    if (signal) query = query.abortSignal(signal);
    const { data, error } = await query.single();
    check();
    if (error) fail("query_failed");
    const preference = mapPreference(data, workspaceId, userId);
    if (!preference || preference.issueId !== targetId) fail("invalid_response");
    return preference;
  });
}

export async function clearPersonalFocus(workspaceId: string, userId: string, signal?: AbortSignal): Promise<void> {
  return withPersonalContext(workspaceId, userId, signal, async ({ client, workspaceId, userId, check }) => {
    let query = client.from("personal_task_focus").delete()
      .eq("workspace_id", workspaceId).eq("user_id", userId).select("workspace_id,user_id");
    if (signal) query = query.abortSignal(signal);
    const { data, error } = await query;
    check();
    if (error) fail("query_failed");
    if ((data ?? []).some((row) => row.workspace_id !== workspaceId || row.user_id !== userId)) fail("invalid_response");
  });
}
